import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Modal, Button, Field, Input, Select, Textarea } from "@/components/ui-kit";
import SignatoryPicker from "@/components/SignatoryPicker";
import { FileText, Plus, Trash2, Pencil, Eye } from "lucide-react";
import PdfViewerModal from "@/components/pdf/PdfViewerModal";
import { todayISO, parseQty, formatIDR } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";

const blankItem = { description: "", slump: "", quantity: 0, unit: "m3", unit_price: 0, note: "" };
const blankForm = {
  po_number: "", date: todayISO(), supplier_name: "", supplier_address: "", supplier_contact: "",
  project: "", payment_term: "Net 30 Days",
  items: [{ ...blankItem }], use_vat: true, vat_rate: 11, discount: 0,
  instructions: "1. Jadwal Pengecoran: (isi tgl & jam).\n2. Pengambilan benda uji beton (silinder) akan dilakukan di lapangan oleh tim QC proyek kami.\n3. Mutu beton yang saat diuji slump tidak sesuai spesifikasi akan ditolak (Reject) tanpa kompensasi.\n4. Harap cantumkan Nomor PO ini pada seluruh Surat Jalan (Delivery Note) dan kelengkapan tagihan.",
  notes: "",
  signatory_prepared: "Procurement Staff",
  signatory_approved: "Direktur Utama",
  signatory_confirmed: "Tanda Tangan & Cap",
  name_prepared: "",
  name_approved: "",
  name_confirmed: "",
};

export default function BatchingPo() {
  const { hasPerm } = useAuth();
  const canCreate = hasPerm("batching.po.create");
  const canEdit = hasPerm("batching.po.edit");
  const canDelete = hasPerm("batching.po.delete");
  const [rows, setRows] = useState([]);
  // Iter 65 (#3) — popout PDF viewer.
  const [viewer, setViewer] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/batching/pos").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const totals = () => {
    const sub = form.items.reduce((a, i) => a + parseQty(i.quantity) * parseQty(i.unit_price), 0);
    const afterDisc = Math.max(sub - parseQty(form.discount), 0);
    const vat = form.use_vat ? afterDisc * (parseQty(form.vat_rate) / 100) : 0;
    return { sub, vat, grand: afterDisc + vat };
  };
  const T = totals();

  const setItem = (i, k, v) => { const items = [...form.items]; items[i] = { ...items[i], [k]: v }; setForm({ ...form, items }); };
  const addItem = () => setForm({ ...form, items: [...form.items, { ...blankItem }] });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, j) => j !== i) });

  const openNew = async () => {
    setEditId(null);
    setForm({ ...blankForm, items: [{ ...blankItem }] });
    setOpen(true);
    // Iter 57 — auto-fill PO number on new form.
    try {
      const r = await api.get("/batching/pos/next-number");
      if (r.data?.po_number) setForm((f) => ({ ...f, po_number: r.data.po_number }));
    } catch (e) { /* user can still type manually */ }
  };
  const openEdit = (po) => {
    setEditId(po.id);
    setForm({
      ...blankForm, ...po,
      items: (po.items || [{ ...blankItem }]).map((x) => ({ ...x })),
      vat_rate: po.vat_rate || 11, discount: po.discount || 0,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.po_number || !form.supplier_name || !form.items[0]?.description) {
      toast.error("No PO, Supplier, dan minimal 1 item wajib diisi"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        vat_rate: parseQty(form.vat_rate),
        discount: parseQty(form.discount),
        items: form.items.map((i) => ({ ...i, quantity: parseQty(i.quantity), unit_price: parseQty(i.unit_price) })),
      };
      if (editId) await api.patch(`/batching/pos/${editId}`, payload);
      else await api.post("/batching/pos", payload);
      toast.success(editId ? "PO diupdate" : "PO Batching dibuat");
      setOpen(false); setEditId(null); setForm(blankForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus PO ini?")) return;
    try { await api.delete(`/batching/pos/${id}`); load(); toast.success("PO dihapus"); }
    catch (e) { toast.error("Gagal hapus"); }
  };

  return (
    <div className="space-y-6">
      <PageHeader overline="Batching Plant" title="PO Vendor" description="Purchase Order ke supplier (semen, agregat, beton ready-mix, sewa pompa, dll)." icon={FileText}
        action={canCreate ? <Button onClick={openNew} data-testid="batching-po-new-btn"><Plus className="h-4 w-4" /> PO Baru</Button> : null} />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-600">
            <tr><th className="text-left py-2 px-3">No PO</th><th className="text-left py-2 px-3">Tanggal</th><th className="text-left py-2 px-3">Supplier</th><th className="text-left py-2 px-3">Proyek</th><th className="text-right py-2 px-3">Grand Total</th><th className="text-right py-2 px-3 w-28">Aksi</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center text-stone-400 py-8 text-xs">Belum ada PO Batching</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/40" data-testid={`batching-po-row-${r.id}`}>
                <td className="py-2 px-3 font-mono">{r.po_number}</td>
                <td className="py-2 px-3">{r.date}</td>
                <td className="py-2 px-3">{r.supplier_name}</td>
                <td className="py-2 px-3 text-stone-600">{r.project || "-"}</td>
                <td className="py-2 px-3 text-right font-mono">{formatIDR(r.grand_total || 0)}</td>
                <td className="py-2 px-3 text-right">
                  <button onClick={() => setViewer(r)} className="text-stone-500 hover:text-terra-600 px-1.5 inline-block" title="Lihat PDF" data-testid={`batching-po-pdf-${r.id}`}><Eye className="h-3.5 w-3.5" /></button>
                  {canEdit && (
                    <button onClick={() => openEdit(r)} className="text-stone-500 hover:text-terra-600 px-1.5" data-testid={`batching-po-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  )}
                  {canDelete && (
                    <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-1.5" data-testid={`batching-po-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit PO Vendor" : "PO Vendor Baru"} size="xl">
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="No. PO" required><Input value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} placeholder="PO-RBB-2606-1004" data-testid="batching-po-number" /></Field>
            <Field label="Tanggal" required><DatePicker value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="Nama Supplier" required><Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="PT. BETON KUAT MANDIRI" data-testid="batching-po-supplier" /></Field>
            <Field label="UP / Kontak Supplier"><Input value={form.supplier_contact} onChange={(e) => setForm({ ...form, supplier_contact: e.target.value })} placeholder="UP: Bpk. Hermawan (Sales)" /></Field>
            <Field label="Alamat Supplier"><Textarea rows={2} value={form.supplier_address} onChange={(e) => setForm({ ...form, supplier_address: e.target.value })} placeholder="Kawasan Industri Terpadu Blok A" /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Proyek"><Input value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} placeholder="Pembangunan Tol Seksi 2" /></Field>
              <Field label="Termin Bayar"><Input value={form.payment_term} onChange={(e) => setForm({ ...form, payment_term: e.target.value })} /></Field>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold uppercase text-stone-600">Item PO</span>
              <Button variant="secondary" className="!py-1 !px-2 text-xs" onClick={addItem}><Plus className="h-3 w-3" /> Tambah</Button>
            </div>
            <div className="border border-stone-200">
              <div className="gap-1.5 p-2 bg-stone-50 text-[10px] uppercase tracking-wider font-semibold text-stone-600 border-b border-stone-200" style={{ display: "grid", gridTemplateColumns: "3fr 1.2fr 1fr 0.8fr 1.5fr 0.4fr" }}>
                <div>Deskripsi (Mutu Beton)</div><div>Slump</div><div className="text-right">Qty</div><div>Unit</div><div className="text-right">Harga Sat.</div><div></div>
              </div>
              {form.items.map((it, i) => (
                <div key={i} className="gap-1.5 p-2 border-b border-stone-100 items-center" style={{ display: "grid", gridTemplateColumns: "3fr 1.2fr 1fr 0.8fr 1.5fr 0.4fr" }}>
                  <div>
                    <Input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder="Ready Mix K-300 NFA" data-testid={`batching-po-item-desc-${i}`} />
                    <Input className="mt-1 text-[11px]" value={it.note} onChange={(e) => setItem(i, "note", e.target.value)} placeholder="Catatan (mis. Kapasitas TM: 7 m³)" />
                  </div>
                  <Input value={it.slump} onChange={(e) => setItem(i, "slump", e.target.value)} placeholder="12 ± 2" />
                  <Input className="text-right font-mono-num" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`batching-po-item-qty-${i}`} />
                  <Select value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)}><option value="m3">m³</option><option value="Ls">Ls</option><option value="kg">kg</option><option value="pcs">pcs</option></Select>
                  <Input className="text-right font-mono-num" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`batching-po-item-price-${i}`} />
                  <button onClick={() => rmItem(i)} className="text-stone-400 hover:text-red-700 justify-self-center"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Instruksi Pengiriman & Syarat (multi-line)">
              <Textarea rows={4} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
            </Field>
            <div className="space-y-2 bg-stone-50 p-3">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.use_vat} onChange={(e) => setForm({ ...form, use_vat: e.target.checked })} /> Pakai PPN <Input className="!w-16 ml-2" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} /> %</label>
              <Field label="Diskon (Rp)"><Input className="font-mono-num" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} /></Field>
              <div className="text-xs text-stone-600 pt-2 border-t border-stone-200 space-y-0.5">
                <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{formatIDR(T.sub)}</span></div>
                <div className="flex justify-between"><span>PPN {form.vat_rate}%</span><span className="font-mono">{formatIDR(T.vat)}</span></div>
                <div className="flex justify-between font-bold text-sm text-stone-900 border-t border-stone-300 pt-1 mt-1"><span>GRAND TOTAL</span><span className="font-mono">{formatIDR(T.grand)}</span></div>
              </div>
            </div>
          </div>
          {/* Iter 54 — Penandatangan (jabatan + nama) */}
          <div className="space-y-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">Penandatangan</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Dibuat Oleh (Jabatan)">
                <Input value={form.signatory_prepared} onChange={(e) => setForm({ ...form, signatory_prepared: e.target.value })} data-testid="batching-po-sig-prepared" />
              </Field>
              <Field label="Disetujui Oleh (Jabatan)">
                <Input value={form.signatory_approved} onChange={(e) => setForm({ ...form, signatory_approved: e.target.value })} data-testid="batching-po-sig-approved" />
              </Field>
              <Field label="Dikonfirmasi (Supplier) (Jabatan)">
                <Input value={form.signatory_confirmed} onChange={(e) => setForm({ ...form, signatory_confirmed: e.target.value })} data-testid="batching-po-sig-confirmed" />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Nama Penandatangan #1">
                <SignatoryPicker name={form.name_prepared}
                  onPick={({ name, position }) => setForm({ ...form, name_prepared: name, signatory_prepared: position || form.signatory_prepared })}
                  data-testid="batching-po-name-prepared" />
              </Field>
              <Field label="Nama Penandatangan #2">
                <SignatoryPicker name={form.name_approved}
                  onPick={({ name, position }) => setForm({ ...form, name_approved: name, signatory_approved: position || form.signatory_approved })}
                  data-testid="batching-po-name-approved" />
              </Field>
              <Field label="Nama Penandatangan #3 (Supplier)">
                <SignatoryPicker name={form.name_confirmed}
                  onPick={({ name, position }) => setForm({ ...form, name_confirmed: name, signatory_confirmed: position || form.signatory_confirmed })}
                  data-testid="batching-po-name-confirmed" />
              </Field>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} disabled={saving} data-testid="batching-po-save-btn">{saving ? "Menyimpan…" : (editId ? "Update PO" : "Simpan PO")}</Button>
          </div>
        </div>
      </Modal>
      {/* Iter 65 (#3) — Popout PDF viewer */}
      <PdfViewerModal
        open={!!viewer}
        onClose={() => setViewer(null)}
        pdfUrl={viewer ? `${process.env.REACT_APP_BACKEND_URL}/api/batching/pos/${viewer.id}/pdf` : ""}
        fileName={`PO-Batching-${viewer?.po_number || viewer?.id || "doc"}.pdf`}
        title={`PO Batching ${viewer?.po_number || ""}`}
      />
    </div>
  );
}
